const HTTP_UNAUTHORIZED = 401;
const HTTP_CLIENT_ERROR_MIN = 400;
const HTTP_CLIENT_ERROR_MAX = 499;
const MAX_RETRY_DELAY_MS = 10_000;
const BASE_RETRY_DELAY_MS = 1_000;

/**
 * Extract HTTP status from error using duck-typing.
 * Supports both ApiError (has .status) and plain Error & { status }
 * (e.g. useFetchToken).
 */
function getHttpStatus(error: Error): number | undefined {
  const status = (error as Error & { status?: number }).status;
  return typeof status === 'number' ? status : undefined;
}

function isClientError(error: Error): boolean {
  const status = getHttpStatus(error);
  return status !== undefined && status >= HTTP_CLIENT_ERROR_MIN && status <= HTTP_CLIENT_ERROR_MAX;
}

function isUnauthorized(error: Error): boolean {
  return getHttpStatus(error) === HTTP_UNAUTHORIZED;
}

interface RetryPolicyOptions {
  /** Max retries for non-4xx errors. Default: 3 */
  maxRetries?: number;
  /** Max retries for 401 (token refresh race). Default: 2. Set 0 to skip. */
  maxRetriesOn401?: number;
}

/**
 * Creates a retry function with consistent 4xx handling.
 * - 401: retry up to maxRetriesOn401 (token refresh race condition)
 * - Other 4xx: never retry (client errors won't fix themselves)
 * - 5xx / network / timeout: retry up to maxRetries
 */
type RetryFn = (failureCount: number, error: Error) => boolean;

export function createRetryFn(options: RetryPolicyOptions = {}): RetryFn {
  const { maxRetries = 3, maxRetriesOn401 = 2 } = options;

  return (failureCount: number, error: Error): boolean => {
    if (isUnauthorized(error)) {
      return failureCount < maxRetriesOn401;
    }
    if (isClientError(error)) {
      return false;
    }
    return failureCount < maxRetries;
  };
}

/** Exponential backoff: 1s, 2s, 4s, 8s, 10s (cap) */
export function standardRetryDelay(attempt: number): number {
  return Math.min(BASE_RETRY_DELAY_MS * 2 ** attempt, MAX_RETRY_DELAY_MS);
}
